import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {
    Arrow,
    BackButton,
    ButtonContainer,
    ErrorText,
    NextButton,
    QuoteEyebrow,
    QuoteFieldStack,
    QuoteGrid,
    QuoteHeader,
    QuoteHeader3,
    QuoteInnerWrapper,
    QuoteInput,
    QuoteInputTitle,
    QuoteInputWrapper,
    RegistrationErrorWrapper,
} from './QuoteElements'
import { Oval } from 'react-loading-icons'
import OTPInput from './OTPInput';
import StoryPanel from './StoryPanel';

/**
 * The card this step lives in.
 *
 * It used to be a near-copy of QuoteInnerWrapper that had drifted — its own
 * warm-grey background, its own radius, and popup props that were never
 * transient, so styled-components forwarded `ispopupopen` to the DOM. There is
 * no popup on this step, so the copy is gone and it just uses the shared rail.
 */
const CustomerInnerWrapper = styled(QuoteInnerWrapper)`
  max-height: calc(100vh - 100px);
`;

const OTPWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

/* The address the code went to, restated so the customer can check it. */
const OTPTarget = styled.div`
    margin-top: 26px;
    padding: 14px 16px;
    border: 1px solid #e0e6ea;
    border-radius: 8px;
    background: #f6fafd;
    font-size: 0.8rem;
    line-height: 1.5;
    color: #41545f;
    word-break: break-word;

    b {
        color: #0f1b24;
        font-weight: 800;
    }
`;

const OTPFieldLabel = styled.span`
    display: block;
    font-size: 0.6rem;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    font-weight: 800;
    color: #8098a8;
    margin: 26px 0 10px;
`;

const Customer = ({
    formState,
    responseState,
    satelliteImageUrl,
    updateFormControl,
    errorFormState,
    nextButton,
    submitting,
    setSubmitting,
    customerErrMesg,
    backButton,
    handleCreateCustomer,
    loadingTexts,
    loadingTextIndex
}) => {
    const [showOTP, setShowOTP] = useState(false);
    const [otp, setOTP] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0)
    }, []);

    useEffect(() => {
        if (showOTP && 'OTPCredential' in window) {
            const abortController = new AbortController();
    
            // Set a timeout to abort after 60 seconds
            const timeout = setTimeout(() => abortController.abort(), 60000);
    
            navigator.credentials
                .get({
                    otp: { transport: ['sms'] },
                    signal: abortController.signal,
                })
                .then((otpCredential) => {
                    if (otpCredential && otpCredential.code) {
                        console.log('OTP received:', otpCredential.code); // Log OTP
                        setOTP(otpCredential.code); // Auto-fill OTP
                    } else {
                        console.error('No OTP received.');
                    }
                    clearTimeout(timeout);
                })
                .catch((error) => {
                    if (error.name !== 'AbortError') {
                        console.error('Error fetching OTP:', error);
                    }
                });
    
            return () => {
                abortController.abort();
                clearTimeout(timeout);
            };
        }
    }, [showOTP]);

    // Function to handle form's Next button click
    const handleNextClick = async () => {
        const createSuccess = await handleCreateCustomer();
        if(createSuccess){
            setShowOTP(true);
        }
    };

    // Function to handle OTP Back button click
    const handleBackClick = () => {
        setShowOTP(false); // Go back to form view
        setOTP(''); // Clear OTP field
        setErrorMessage(''); // Clear error messages
    };

    // Function to handle OTP submission
    const handleOTPSubmit = async () => {
        try {
            setSubmitting(true);
            const response = await fetch(process.env.REACT_APP_API_URL + '/estimate/verifyOtp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    address: formState.address, 
                    oneTimePassword: otp
                }), // Send OTP to backend for verification
            });

            if (response.ok) {
                const data = await response.json();
                if (data.verified) {
                    nextButton(); // Trigger the original nextButton function if OTP verified
                } else {
                    setErrorMessage('Invalid OTP. Please try again.');
                }
            } else {
                setErrorMessage('Failed to verify OTP. Please try again.');
            }
        } catch (error) {
            setErrorMessage('An error occurred. Please try again.');
            console.error("OTP Verification Error:", error);
        }
        setSubmitting(false);
    };

    useEffect(() => {
        if (otp.length === 6) {
            handleOTPSubmit(); // Automatically submit once OTP is complete
        }
    }, [otp]);

  return (
    <QuoteGrid>
        {/* The estimate finishes before the code screen appears, so by then the
            panel has the customer's own roof and their estimated system size to
            show them while they wait. */}
        <StoryPanel
            step={showOTP ? 'otp' : 'contact'}
            satelliteImageUrl={satelliteImageUrl}
            address={formState.address}
            estimateSize={responseState?.estimateSize}
            solarProductionMonthly={responseState?.solarProductionMonthly}
        />
        <CustomerInnerWrapper>
            {!showOTP &&
                <BackButton onClick={backButton}>
                    <Arrow>&lt;</Arrow>
                    Back
                </BackButton>
            }
            
            {!showOTP ? (
                <>
                <QuoteEyebrow>Step 2 of 3</QuoteEyebrow>
                <QuoteHeader>Where should we send it?</QuoteHeader>
                <QuoteHeader3>
                    We'll send a code to confirm it's you, then show you your pricing.
                </QuoteHeader3>
                <QuoteFieldStack>
                    <QuoteInputWrapper>
                        <QuoteInputTitle htmlFor='name'>Name</QuoteInputTitle>
                        <QuoteInput type='text' id='name' value={formState.name} onChange={updateFormControl} autoComplete='name' required/>
                    </QuoteInputWrapper>
                    <QuoteInputWrapper>
                        <QuoteInputTitle htmlFor='email'>Email</QuoteInputTitle>
                        <QuoteInput type='email' id='email' value={formState.email} onChange={updateFormControl} autoComplete='email' inputMode='email' required/>
                    </QuoteInputWrapper>
                    <QuoteInputWrapper>
                        <QuoteInputTitle htmlFor='phone'>Phone</QuoteInputTitle>
                        <QuoteInput type='text' id='phone' value={formState.phone} onChange={updateFormControl} autoComplete='tel' inputMode='tel' required/>
                    </QuoteInputWrapper>
                </QuoteFieldStack>
                {!submitting &&
                    <ButtonContainer>
                        <NextButton onClick={handleNextClick} disabled={!formState.name || !errorFormState.hasEmailAndIsValid || !errorFormState.hasPhoneAndIsValid}>Send my code</NextButton>
                    </ButtonContainer>
                }
                {submitting &&
                    <>
                        <ButtonContainer>
                            <NextButton disabled>
                                <Oval height='1.5rem'/>
                            </NextButton>
                        </ButtonContainer>
                        <RegistrationErrorWrapper>
                            {loadingTexts[loadingTextIndex]}
                        </RegistrationErrorWrapper>
        
                    </>                 
                }
                {customerErrMesg &&
                    <RegistrationErrorWrapper>
                        <ErrorText>{customerErrMesg}</ErrorText>
                    </RegistrationErrorWrapper>
                }
            </>
            ) : (
                <OTPWrapper>
                    <BackButton onClick={handleBackClick}>
                        <Arrow>&lt;</Arrow> Back
                    </BackButton>
                    <QuoteEyebrow>Step 2 of 3 &middot; Confirm</QuoteEyebrow>
                    <QuoteHeader>Confirm It's You</QuoteHeader>
                    <QuoteHeader3>
                        We sent a six-digit code. Enter it below and your pricing opens up.
                    </QuoteHeader3>
                    <OTPTarget>
                        Sent to <b>{formState.email}</b>
                        {formState.phone ? <> and <b>{formState.phone}</b></> : null}
                    </OTPTarget>
                    <QuoteInputWrapper>
                        <OTPFieldLabel>One-Time Code</OTPFieldLabel>
                        <OTPInput length={6} setOTP={setOTP} />
                    </QuoteInputWrapper>
                    <ButtonContainer>
                        {!submitting &&
                            <NextButton onClick={handleOTPSubmit} disabled={otp.length !== 6}>
                                Verify
                            </NextButton>
                        }
                        {submitting &&
                            <NextButton disabled>
                                <Oval height='1.5rem'/>
                            </NextButton>
                        }
                    </ButtonContainer>
                    {errorMessage && (
                        <RegistrationErrorWrapper>
                            <ErrorText>{errorMessage}</ErrorText>
                        </RegistrationErrorWrapper>
                    )}
                </OTPWrapper>
            )}

        </CustomerInnerWrapper>
    </QuoteGrid>
  )
}

export default Customer